import { LoadingButton } from "@mui/lab";
import { Avatar, Box, Button, Grid, Link, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import logo from "assets/logos/logo-transparent.png";
import profile from "assets/splashpage/profile.jpg";
import purpleImg from "assets/splashpage/purplemesh2.png";
import { HEART_COLOR } from "styles/oneOffColor";

import { VA_MONEY_BACK_GUARANTEE } from "constants/influencer";
import { getSource, trackEvent } from "utils/tracking";

import styles from "./styles";

interface Props {
  checkoutBundle: () => Promise<void>;
  fetchUserLoading: boolean;
  hasAccessToVaYearly?: boolean;
  hasBookCall: boolean;
}

const PROGRAM_START = new Date("2026-03-22T00:00:00Z");
const PROGRAM_END = new Date("2026-04-07T00:00:00Z");

const isProgramOpen = () => {
  const now = new Date();
  return now >= PROGRAM_START && now < PROGRAM_END;
};

const ProgramInfo = ({
  checkoutBundle,
  fetchUserLoading,
  hasAccessToVaYearly,
  hasBookCall,
}: Props) => {
  const trackTermsAndCondition = () => {
    trackEvent("Virtual Assistant Money Back - Terms & Conditions Clicked", {
      source: getSource(),
      clickedFrom: "Virtual Assistant Money Back - Splash Page",
    });
  };

  return (
    <Box sx={{ my: 3, px: { xs: 2, md: 6, lg: 10 } }}>
      <Link
        href={"https://www.onbento.com?s=va_money_back"}
        target="_blank"
        rel="noreferrer"
      >
        <Box component="img" src={logo} sx={{ width: 70, mt: 6, mb: 3 }} />
      </Link>
      <Typography variant="h1" sx={styles.header} component="div">
        <strong>
          Your Virtual Assistant <br />
          For Brand Pitches
        </strong>
      </Typography>

      <Typography>
        <em>
          👉 We pitch brands for you, follow up, and help you negotiate paid
          deals.
        </em>
      </Typography>

      <Typography variant="h6" sx={{ my: 2 }}>
        What you'll get
      </Typography>

      <ul>
        <em>
          {" "}
          <li>
            📧 300+ emails sent for you every month — without writing a single
            pitch.
          </li>
          <li>
            💸 Exclusive brand pay data - know exactly what brands pay (so you
            never undersell again)
          </li>
          <li>💬 Tier-one customer support — fast help from real humans.</li>
        </em>
      </ul>

      <Box
        sx={{
          backgroundImage: `url(${purpleImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          p: 3,
          borderRadius: 3,
          fontSize: 14,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "green.main" }}>
          💸 12-Month Money Back Guarantee
        </Typography>

        <Typography>
          If you don't earn $650+ in brand deals sourced by Bento, we'll refund
          you. No questions.
        </Typography>
      </Box>

      <Grid container justifyContent={"center"} sx={{ mt: 3 }} gap={1}>
        <LoadingButton
          onClick={checkoutBundle}
          variant="contained"
          loading={fetchUserLoading}
          disabled={!isProgramOpen()}
          sx={{
            backgroundColor: "yellow.main",
            borderRadius: 4,
            px: 3,
            py: 0.5,
            color: "black",
            fontSize: 16,
            "&:hover": {
              backgroundColor: "yellow.light",
            },
            textTransform: "none",
          }}
        >
          👉 Start Pitching For Me
        </LoadingButton>
        {hasBookCall && (
          <Button
            variant="outlined"
            href="https://calendly.com/jaclyn-bento/bento-s-virtual-assistant-info-meeting"
            target="_blank"
            onClick={() =>
              trackEvent("Virtual Assistant Money Back - Book a Call Clicked")
            }
            sx={{
              color: "purple.main",
              borderColor: "purple.main",
              borderRadius: 4,
              px: 3,
              py: 0.5,
              textTransform: "none",
            }}
          >
            Book a call
          </Button>
        )}
      </Grid>

      {hasAccessToVaYearly ? (
        <Grid container alignItems="center" justifyContent={"center"}>
          <Typography
            textAlign={"center"}
            sx={{ color: grey[600], fontSize: 12, mt: 2 }}
          >
            You have already purchased the Virtual Assistant Annual Plan.
          </Typography>
        </Grid>
      ) : (
        <Grid container alignItems="center" justifyContent={"center"}>
          <Typography
            textAlign={"center"}
            sx={{ color: grey[600], fontSize: 12, mt: 2 }}
          >
            <em>
              ✔️ Risk Free for 12 Months. Offer ends: April 6.
              <br />
              <Link
                href={VA_MONEY_BACK_GUARANTEE}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackTermsAndCondition}
                sx={{ ml: 0.5 }}
              >
                Terms & Conditions Apply
              </Link>
            </em>
          </Typography>
        </Grid>
      )}

      <Grid container justifyContent={"center"}>
        <Grid
          container
          alignItems="center"
          onClick={() => window.open("https://www.instagram.com/sydneymohni")}
          sx={{
            cursor: "pointer",
            mt: 3,
            px: 2,
            py: 1,
            maxWidth: 500,
            backgroundColor: "white",
            borderRadius: 3,
            "&:hover": {
              boxShadow: 2,
            },
          }}
          boxShadow={1}
          gap={1}
        >
          <Grid item xs="auto">
            <Avatar
              src={profile}
              alt="User Avatar"
              sx={{ width: 50, height: 50 }}
            />
          </Grid>
          <Grid item xs>
            <strong>sydneymohni</strong> I get all my students started on bento
            - in fact the comment section here is FULL of their stories. I have
            students earning anywhere from $1000 - $4000 within their first
            month of pitching all because of bento
          </Grid>
          <Grid item xs="auto">
            <Box
              component="i"
              className="fa-solid fa-heart"
              sx={{ color: HEART_COLOR }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Typography textAlign="center" variant="h6" sx={{ mt: 3 }}>
        <em>
          "My students earn{" "}
          <Box component="span" sx={{ color: "purple.main" }}>
            $1000 - $4000
          </Box>{" "}
          within their first month... all because of Bento."
        </em>
      </Typography>
    </Box>
  );
};

export default ProgramInfo;
